"use client";

import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";

import { useLanguage } from "../i18n/LanguageProvider";

/* Daftar spesialisasi DIHASILKAN, bukan diketik. Berkas ini dibuat
   oleh `node sinkron-jadwal.mjs` - lihat catatan panjang di bawah. */
import daftarSpesialisasi from "../jadwal-dokter/spesialisasi.json";

/* ============================================================
   DOCTOR SEARCH

   Panel pencarian dokter, dipasang menimpa bagian bawah hero.

   PANEL INI SUDAH TERSAMBUNG.

   Tombol "Cari Dokter" mengarahkan ke /jadwal-dokter dengan query
   param, dan halaman itu MEMBACANYA - lihat useSearchParams di
   app/jadwal-dokter/JadwalDokterContent.jsx.

     /jadwal-dokter?nama=fahron&spesialisasi=penyakit-dalam&hari=senin

   Kalau suatu saat halaman tujuan diubah dan berhenti membaca param
   itu, panel ini akan terlihat rusak tanpa satu pun error:
   pengunjung mengisi form lalu mendarat di daftar yang sama persis.
   Uji cepatnya adalah membuka URL di atas langsung di peramban.

   KENAPA TIDAK ADA FIELD "RUMAH SAKIT"
   Header referensi (RS Pondok Indah) punya field itu karena mereka grup
   dengan beberapa rumah sakit. RS JMC satu rumah sakit, jadi dropdown
   berisi satu pilihan hanya menambah langkah tanpa menyaring apa pun.
   ============================================================ */

const SEARCH_TARGET = "/jadwal-dokter";

/* ============================================================
   DAFTAR SPESIALISASI - DIHASILKAN DARI DATA, BUKAN DIKETIK

   Isinya datang dari app/jadwal-dokter/spesialisasi.json, yang
   dibuat `node sinkron-jadwal.mjs` dari daftar dokter yang
   benar-benar praktik.

   KENAPA TIDAK DITULIS MANUAL LAGI

   Versi sebelumnya berisi 11 spesialisasi yang saya karang sebagai
   contoh. Setelah data asli masuk, ia terbukti menyimpang:

     panel                    data asli
     Kebidanan & Kandungan -> Obstetri & Ginekologi
     THT-KL                -> THT - Bedah Kepala Leher
     Gigi & Mulut          -> Gigi
     Dokter Umum           -> Umum
     Bedah (satu)          -> Bedah Umum, Ortopedi, Toraks (tiga)
     (tidak ada)           -> Ginjal & Hipertensi, Paru, Urologi

   Tiap penyimpangan berarti pengunjung memilih sesuatu lalu
   mendapat nol hasil - tanpa pesan error, karena secara teknis
   memang tidak ada yang rusak.

   Sekarang tidak mungkin meleset lagi: satu-satunya sumber nama
   spesialisasi adalah PETA_POLI di normalisasi.mjs, dan panel ini
   membacanya lewat berkas hasil sinkron. Poli baru di SIMRS
   otomatis muncul di sini setelah sinkron berikutnya.

   ⚠️ BUILD GAGAL "Cannot find module '../jadwal-dokter/spesialisasi.json'"
   berarti berkasnya belum ada. Jalankan `node sinkron-jadwal.mjs`.

   ------------------------------------------------------------
   LABELNYA TIDAK LEWAT KAMUS

   Nama spesialisasi datang dari datanya sendiri, yang sudah
   menyediakan versi Indonesia dan Inggris. Karena itu kunci
   `cari.sp.*` di dictionary.js TIDAK LAGI DIPAKAI untuk daftar ini.

   Kuncinya sengaja dibiarkan di kamus - tidak merugikan apa pun.
   Tapi kamu perlu tahu: menyunting cari.sp.* sekarang TIDAK akan
   mengubah apa pun di layar. Nama spesialisasi diubah di PETA_POLI
   pada normalisasi.mjs.

   `slug` masuk ke URL (?spesialisasi=bedah-ortopedi) dan dibaca
   halaman tujuan. Ia sengaja tidak diterjemahkan: kalau ikut
   berubah saat bahasa Inggris, tautan yang dibagikan pengunjung
   akan rusak begitu penerimanya membuka situs dalam bahasa lain.
   ============================================================ */

const DAYS = [
  { value: "senin", key: "senin" },
  { value: "selasa", key: "selasa" },
  { value: "rabu", key: "rabu" },
  { value: "kamis", key: "kamis" },
  { value: "jumat", key: "jumat" },
  { value: "sabtu", key: "sabtu" },
  { value: "minggu", key: "minggu" },
];


const EMPTY = { nama: "", spesialisasi: "", hari: "" };

/* ============================================================
   EFEK SCRIM (halaman meredup saat panel aktif)

   Set false untuk mematikannya.

   PERTIMBANGAN SEBELUM MEMAKAI:
   Scrim dipicu oleh fokus, dan fokus tidak hanya datang dari klik.
   Pengguna keyboard yang menekan Tab dari header akan tiba-tiba melihat
   seluruh halaman meredup tanpa melakukan apa-apa. Efek yang sama juga
   muncul saat pembaca layar memindah fokus.

   Itu bukan alasan untuk tidak memakainya - efeknya memang mengarahkan
   perhatian - tapi kalau nanti ada keluhan "layarnya tiba-tiba gelap",
   inilah penyebabnya.
   ============================================================ */
const ENABLE_SCRIM = true;

export default function DoctorSearch() {
  const router = useRouter();
  const { lang, t } = useLanguage();
  const [form, setForm] = useState(EMPTY);
  const [active, setActive] = useState(false);
  const panelRef = useRef(null);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  /* Label mengikuti bahasa aktif; `value` selalu slug yang sama. */
  const opsiSpesialisasi = daftarSpesialisasi.spesialisasi.map((sp) => ({
    value: sp.slug,
    label: lang === "en" ? sp.en : sp.id,
  }));

  /* Esc melepas fokus dan menutup scrim. Tanpa ini, satu-satunya cara
     keluar adalah mengklik area lain - menyulitkan pengguna keyboard. */
  useEffect(() => {
    if (!active) return;

    const onKey = (e) => {
      if (e.key === "Escape") {
        setActive(false);
        panelRef.current?.querySelector(":focus")?.blur();
      }
    };

    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [active]);

  /* onFocus/onBlur di React memetakan ke focusin/focusout, yang MERAMBAT
     naik dari elemen anak. Itu sebabnya cukup dipasang di container, tidak
     perlu di tiap input.

     Pemeriksaan relatedTarget penting: tanpa itu, berpindah dari kolom
     nama ke kolom spesialisasi akan memicu blur sesaat dan scrim berkedip
     mati-nyala di antara dua field. */
  const onFocusIn = () => setActive(true);
  const onFocusOut = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) setActive(false);
  };

  const onSubmit = (e) => {
    e.preventDefault();

    /* Field kosong tidak ikut masuk URL. Tanpa penyaringan ini, URL-nya
       jadi ?nama=&spesialisasi=&hari= - jelek dibaca, dan halaman tujuan
       harus repot membedakan "kosong" dari "tidak diisi". */
    const params = new URLSearchParams(
      Object.entries(form).filter(([, value]) => value.trim() !== "")
    );

    const query = params.toString();
    router.push(query ? `${SEARCH_TARGET}?${query}` : SEARCH_TARGET);
  };

  return (
    <>
      {/* SCRIM
         z-[55] menaruhnya DI ATAS header (z-50), sehingga header ikut
         meredup seperti pada referensi. Kalau header ingin tetap terang,
         turunkan ke z-40.

         aria-hidden + pointer-events: lapisan ini murni visual, tapi tetap
         bisa diklik untuk menutup. */}
      {ENABLE_SCRIM && (
        <div
          aria-hidden="true"
          onClick={() => setActive(false)}
          className={`fixed inset-0 z-[55] bg-slate-950/45 transition-opacity duration-300 motion-reduce:transition-none ${
            active ? "opacity-100" : "pointer-events-none opacity-0"
          }`}
        />
      )}

      <div
        ref={panelRef}
        onFocus={onFocusIn}
        onBlur={onFocusOut}
        className={`relative ${active ? "z-[60]" : "z-20"}`}
      >
        {/* PANEL SATU KOTAK.

           Versi sebelumnya membuat tab sebagai elemen terpisah di atas
           panel, sehingga area di kanan tab jadi kosong dan hero terlihat
           menembus di sana. Sekarang panelnya satu kotak putih utuh, dan
           "Cari Dokter" hanya segmen berwarna DI DALAM baris atasnya. */}
        <div className="overflow-hidden rounded-2xl bg-[var(--c-surface)] shadow-[0_1px_2px_rgba(15,23,42,0.16),0_12px_24px_-10px_rgba(15,23,42,0.2),0_32px_60px_-20px_rgba(15,23,42,0.28)] ring-1 ring-black/5 sm:rounded-[24px] md:rounded-[28px]">

          <div className="flex">
            {/* border-b-2 plum menandai tab aktif. Sisa barisnya memakai
               border-b biasa berwarna abu supaya garisnya menyambung
               sampai tepi kanan, tidak putus di ujung tab. */}
            <span className="border-b-2 border-[var(--c-brand-mid)] bg-[var(--c-tint)] px-3.5 py-2 text-xs font-bold text-[var(--c-brand)] sm:px-8 sm:py-3.5 sm:text-sm md:px-10 md:py-4 md:text-base">
              {t("cari.tab")}
            </span>
            <div aria-hidden="true" className="flex-1 border-b border-[var(--c-line-soft)]" />
          </div>

          <form onSubmit={onSubmit} className="p-3.5 sm:p-6 md:p-9">

            {/* SKALA RESPONSIF
               Tiga tingkat: mobile -> sm -> md. Versi sebelumnya melompat
               langsung dari mobile ke md, jadi di layar 400-640px semuanya
               masih memakai ukuran desktop. */}
            {/* GRID 2 KOLOM DI MOBILE.

               Sebelumnya tiga field bertumpuk jadi tiga baris. Spesialisasi
               dan Hari Praktik keduanya dropdown pendek, jadi muat
               berdampingan - hemat satu baris penuh.

               Nama Dokter tetap membentang penuh (col-span-2) karena isinya
               teks bebas yang butuh ruang ketik. */}
            <div className="grid grid-cols-2 gap-2.5 sm:gap-4 md:grid-cols-3 md:gap-6">

        <Field label={t("cari.nama")} htmlFor="cari-nama" className="col-span-2 md:col-span-1">
          <div className="relative">
            <SearchIcon />
            <input
              id="cari-nama"
              type="text"
              value={form.nama}
              onChange={update("nama")}
              placeholder={t("cari.namaPlaceholder")}
              className="w-full rounded-lg border border-[var(--c-line)] bg-[var(--c-surface)] py-1.5 pl-8 pr-2.5 text-xs text-[var(--c-ink)] placeholder:text-slate-400 focus:border-[var(--c-brand-mid)] focus:outline-none focus:ring-2 focus:ring-[var(--c-brand-mid)]/20 sm:rounded-xl sm:py-2.5 sm:pl-10 sm:text-sm md:py-3.5 md:pl-12 md:text-base"
            />
          </div>
        </Field>

        <Field label={t("cari.spesialisasi")} htmlFor="cari-spesialisasi" className="col-span-1">
          <Select
            id="cari-spesialisasi"
            value={form.spesialisasi}
            onChange={update("spesialisasi")}
            placeholder={t("cari.spesialisasiPlaceholder")}
            options={opsiSpesialisasi}
            t={t}
          />
        </Field>

        <Field label={t("cari.hari")} htmlFor="cari-hari" className="col-span-1">
          <Select
            id="cari-hari"
            value={form.hari}
            onChange={update("hari")}
            placeholder={t("cari.hariPlaceholder")}
            options={DAYS}
            prefix="cari.hariNama"
            t={t}
          />
        </Field>

      </div>

            {/* TOMBOL BERDAMPINGAN, BUKAN BERTUMPUK.

               Dua tombol full-width yang menumpuk memakan sekitar 100px
               tinggi di mobile - penyumbang terbesar kesan "terlalu besar".
               Berdampingan, keduanya cukup satu baris.

               Reset dibuat sesempit isinya (shrink-0), Cari Dokter mengisi
               sisa ruang (flex-1). Aksi utama jadi target sentuh terbesar,
               dan urutannya tetap Reset dulu supaya tidak tertekan
               tidak sengaja saat ibu jari meraih tombol cari. */}
            <div className="mt-3 flex gap-2 sm:mt-5 sm:justify-end sm:gap-3 md:mt-8 md:gap-4">
              {/* type="button" WAJIB. Tombol di dalam <form> tanpa type
                 dianggap submit oleh browser, sehingga Reset justru
                 menjalankan pencarian. */}
              <button
                type="button"
                onClick={() => setForm(EMPTY)}
                className="shrink-0 rounded-lg border border-[var(--c-line)] px-4 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-brand-mid)] sm:rounded-xl sm:px-6 sm:text-sm md:px-10 md:py-3.5 md:text-base"
              >
                {t("cari.reset")}
              </button>

              <button
                type="submit"
                className="flex-1 rounded-lg bg-[var(--c-brand)] px-5 py-2 text-xs font-bold text-white transition hover:bg-[var(--c-panel)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-brand-mid)] sm:flex-none sm:rounded-xl sm:px-8 sm:text-sm md:px-14 md:py-3.5 md:text-base"
              >
                {t("cari.submit")}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
function Field({ label, htmlFor, children, className = "" }) {
  return (
    <div className={className}>
      <label
        htmlFor={htmlFor}
        className="sr-only sm:not-sr-only sm:mb-2 sm:block sm:text-xs sm:font-semibold sm:text-slate-700 md:text-sm"
      >
        {label}
      </label>
      {children}
    </div>
  );
}

function Select({ id, value, onChange, placeholder, options, prefix, t }) {
  return (
    <div className="relative">
      <select
        id={id}
        value={value}
        onChange={onChange}
        className={`w-full appearance-none rounded-lg border border-[var(--c-line)] bg-[var(--c-surface)] py-1.5 pl-2.5 pr-8 text-xs focus:border-[var(--c-brand-mid)] focus:outline-none focus:ring-2 focus:ring-[var(--c-brand-mid)]/20 sm:rounded-xl sm:py-2.5 sm:pl-4 sm:text-sm md:py-3.5 md:pr-11 md:text-base ${
          value ? "text-[var(--c-ink)]" : "text-[var(--c-faint)]"
        }`}
      >
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value} className="text-[var(--c-ink)]">
            {/* Dua sumber label, disengaja:
                 option.label - sudah berupa teks, dipakai daftar
                                spesialisasi yang datang dari data
                 prefix.key   - dicari di kamus, dipakai daftar hari
                                yang memang tetap dan hidup di kamus */}
            {option.label ?? t(`${prefix}.${option.key}`)}
          </option>
        ))}
      </select>

      <svg
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
        className="pointer-events-none absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--c-faint)] sm:right-3 sm:h-4 sm:w-4 md:right-4 md:h-5 md:w-5"
      >
        <path
          d="m6 9 6 6 6-6"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
}

function SearchIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className="pointer-events-none absolute left-2.5 top-1/2 h-3 w-3 -translate-y-1/2 text-[var(--c-faint)] sm:left-3 sm:h-4 sm:w-4 md:left-4 md:h-5 md:w-5"
    >
      <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
      <path d="m20 20-3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}
